import React from "react";
import * as c from "./lpTwoColSection.module.scss";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import classNames from "classnames";
import { processMarkdown } from "@utils/markdown";
import { ContentfulCta } from "@components/shared/cta";
import WistiaVideoPopover from "@components/wistia/wistiaVideoPopover";
import { IconPlayAlt } from "@components/icons/iconPlayAlt";

const Image = ({ desktopImage, mobileImage, alt = "" }) => {
  /**
   * Remove bg=transparent from GIF URLs to prevent GIFs from being cut short.
   * The bg=transparent parameter causes Contentful to truncate animated GIFs.
   */
  const processGif = (imageData) => {
    if (!imageData?.images) return imageData;

    // Check if any source contains GIF format
    const hasGifSource =
      imageData.images.sources?.some((source) => source.srcSet?.includes("fm=gif")) ||
      imageData.images.fallback?.src?.includes("fm=gif");

    if (hasGifSource) {
      const processedImages = { ...imageData.images };

      // Process fallback if it exists
      if (processedImages.fallback) {
        const fallback = { ...processedImages.fallback };
        if (fallback.src) {
          fallback.src = fallback.src.replace(/&bg=transparent/g, "");
        }
        if (fallback.srcSet) {
          fallback.srcSet = fallback.srcSet.replace(/&bg=transparent/g, "");
        }
        processedImages.fallback = fallback;
      }

      // Process sources array if it exists
      if (processedImages.sources) {
        processedImages.sources = processedImages.sources.map((source) => ({
          ...source,
          srcSet: source.srcSet ? source.srcSet.replace(/&bg=transparent/g, "") : source.srcSet,
        }));
      }

      return {
        ...imageData,
        images: processedImages,
      };
    }
    return imageData;
  };

  return (
    <>
      <GatsbyImage image={processGif(getImage(desktopImage))} className={c.desktopImage} alt={alt} />
      {mobileImage && (
        <GatsbyImage image={processGif(getImage(mobileImage))} className={c.mobileImage} alt={`${alt} mobile`} />
      )}
    </>
  );
};

const WithConditonalWistiaPopover = ({ wistiaVideoId, children }) =>
  wistiaVideoId ? (
    <WistiaVideoPopover className={c.videoWrapper} wistiaVideoId={wistiaVideoId}>
      <IconPlayAlt className={c.playButton} />
      {children}
    </WistiaVideoPopover>
  ) : (
    children
  );

export default function LpTwoColSection({
  className,
  label,
  heading,
  subheading,
  sectionId,
  shouldShowImageColumnFirst,
  wistiaVideoId = null,
  desktopImage,
  mobileImage,
  ctas = [],
}) {
  const subheadingHtml = processMarkdown(subheading?.subheading);

  // This is pretty ugly, but we need a way to remove the left-margin offset we add to links
  // of this type so that they will be correctly left/center aligned with the section
  const ctaHasDesktopOffset = (cta) =>
    cta?.__typename === "ContentfulCtaButton" && cta?.showIcon && cta?.style[0] === "Link";

  return (
    <section
      id={sectionId}
      className={classNames(className, c.container, { [c.reverseOrder]: shouldShowImageColumnFirst })}
    >
      <div className={c.textContainer} data-transition="slide-enter-child">
        {label && <p className={classNames(c.label, "eyebrow")}>{label}</p>}
        <h2 className={classNames(c.heading, "h2", !label && c.smallerHeadline)}>{heading}</h2>
        <div
          className={classNames(c.subheading, !label && c.smallerSubheading, "p2")}
          dangerouslySetInnerHTML={{ __html: subheadingHtml }}
        />
        {ctas && ctas.length > 0 && (
          <div className={c.ctaContainer}>
            {ctas.map((cta) => (
              <ContentfulCta className={ctaHasDesktopOffset(cta) && c.ctaOffset} key={cta.id} {...cta} />
            ))}
          </div>
        )}
      </div>
      <div className={classNames(c.imageContainer, { [c.usesMobileImage]: mobileImage })} data-transition="slide-enter">
        <WithConditonalWistiaPopover wistiaVideoId={wistiaVideoId}>
          <Image desktopImage={desktopImage} mobileImage={mobileImage} alt={label || heading || ""} />
        </WithConditonalWistiaPopover>
      </div>
    </section>
  );
}
